/**
 * ConsentExpiryBanner — reusable, capability-agnostic "renew soon"
 * notification rendered above any surface that hosts a 3rd-party consent.
 *
 * Solves a documented UX gap: the BE already exposes ``consent_expires_at``
 * (or ``delegation_expires_at`` for cassetto-style flows) on every
 * connection summary, but no FE surface used to alert the user before the
 * consent went stale. PSD2 consents (180 days), AdE delega cassetto
 * (4-year horizon), OAuth refresh tokens (90 days for Fisconline / SF
 * sandbox) — all of them benefit from the same 14d / 7d / 2d escalation.
 *
 * Design notes
 * -------------
 *   - Three semantic states drive both the colour and the ARIA role:
 *       • ≤14d → "info"     (calm cream/ink — non-urgent surfacing)
 *       • ≤7d  → "warning"  (Ando warning token — amber)
 *       • ≤2d  → "critical" (Ando error token — assertive)
 *   - Hidden by design when more than 14 days remain — banners that
 *     always render train users to ignore them (no Cry Wolf UX).
 *   - DRY: a single ``<CapabilityLabel>`` slot keeps copy capability-only
 *     (open_banking / cassetto_fiscale / salesforce …) — vendor names
 *     never leak into customer-facing strings.
 *   - A11y: ``role="status"`` + ``aria-live="polite"`` for info/warning;
 *     ``role="alert"`` + ``aria-live="assertive"`` for critical so screen
 *     readers announce it on mount even when the user is not focused on
 *     the banner.
 *   - The renew action is a callback prop, NOT a hardcoded route — the
 *     host page (Finance hub, Integrations list) knows whether renewing
 *     means "reauth via OAuth", "re-sign delega via SPID", or "rotate
 *     Fisconline password". The chip just says "Rinnova" and delegates.
 *
 * ⚠️ DO NOT pass OAuth ACCESS-TOKEN expiry (``connection.expires_at``)
 * into ``expiresAt``. Access tokens refresh silently every ~1h and are
 * meaningless to the user — passing them here generated "consenso scade
 * domani" false-positives for Gmail/Calendar/Slack/Drive on June 4 2026.
 * Only pass ``consent_expires_at`` (PSD2) / ``delegation_expires_at``
 * (AdE Cassetto) — fields the BE populates ONLY for capabilities that
 * issue a real user-visible consent with a hard deadline.
 */
import { AlertTriangle, Clock4, ShieldAlert } from "lucide-react";
import type * as React from "react";

import { Button } from "../../primitives/button";
import { cn } from "../../lib/cn";

const DAY_MS = 24 * 60 * 60 * 1000;

export type ConsentExpiryUrgency = "info" | "warning" | "critical";

interface UrgencyPresentation {
  readonly icon: React.ReactNode;
  readonly classes: string;
  readonly role: "status" | "alert";
  readonly liveness: "polite" | "assertive";
}

const PRESENTATION: Readonly<Record<ConsentExpiryUrgency, UrgencyPresentation>> = {
  info: {
    icon: <Clock4 className="h-4 w-4 shrink-0 text-ink-2" aria-hidden />,
    classes: "border-hairline bg-card/80 text-ink-1",
    role: "status",
    liveness: "polite",
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4 shrink-0 text-warning" aria-hidden />,
    classes: "border-warning/30 bg-warning/[0.06] text-ink-1",
    role: "status",
    liveness: "polite",
  },
  critical: {
    icon: <ShieldAlert className="h-4 w-4 shrink-0 text-error" aria-hidden />,
    classes: "border-error/30 bg-error/[0.07] text-ink-1",
    role: "alert",
    liveness: "assertive",
  },
};

/**
 * Pure helper exported so callers (and tests) can branch UI on the urgency
 * without re-implementing the threshold logic. Returns ``null`` when the
 * consent is healthy (>14d remaining), expired (negative days), or the
 * ``expiresAt`` is missing — the banner shouldn't render in those cases.
 */
export function consentExpiryUrgency(
  expiresAt: string | Date | null | undefined,
  now: Date = new Date(),
): ConsentExpiryUrgency | null {
  if (!expiresAt) return null;
  const expiry = typeof expiresAt === "string" ? new Date(expiresAt) : expiresAt;
  if (Number.isNaN(expiry.getTime())) return null;
  const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / DAY_MS);
  if (daysRemaining <= 0) return null;       // already expired — different UX path
  if (daysRemaining <= 2) return "critical";
  if (daysRemaining <= 7) return "warning";
  if (daysRemaining <= 14) return "info";
  return null;                                // > 14 days: silent — no Cry Wolf
}

function formatItalianDays(days: number): string {
  return days === 1 ? "domani" : `tra ${days} giorni`;
}

export interface ConsentExpiryBannerProps {
  /**
   * ISO 8601 string or Date when the consent expires. ``null``/``undefined``
   * → banner renders nothing (consent state unknown — host can show a
   * different surface for that).
   */
  readonly expiresAt: string | Date | null | undefined;
  /**
   * Capability-only label shown in copy (``"Conti bancari"`` /
   * ``"Cassetto Fiscale"`` / ``"Salesforce"``). NEVER pass a vendor name
   * here — copy is end-user facing.
   */
  readonly capabilityLabel: string;
  /** Click handler for the Rinnova CTA — host decides the renewal route. */
  readonly onRenew?: () => void;
  /** Override "now" for tests / SSR. */
  readonly now?: Date;
  readonly className?: string;
}

export const ConsentExpiryBanner: React.FC<ConsentExpiryBannerProps> = ({
  expiresAt,
  capabilityLabel,
  onRenew,
  now,
  className,
}) => {
  const reference = now ?? new Date();
  const urgency = consentExpiryUrgency(expiresAt, reference);
  if (urgency === null) return null;
  // Recompute days for the human-readable phrase (kept here to keep the
  // helper purely "urgency level" — the phrase wants the raw count too).
  const expiry = typeof expiresAt === "string" ? new Date(expiresAt!) : (expiresAt as Date);
  const daysRemaining = Math.ceil((expiry.getTime() - reference.getTime()) / DAY_MS);
  const p = PRESENTATION[urgency];
  return (
    <aside
      role={p.role}
      aria-live={p.liveness}
      className={cn(
        "flex items-center gap-3 rounded-md border px-3 py-2 text-[12px]",
        p.classes,
        className,
      )}
    >
      {p.icon}
      <p className="min-w-0 flex-1">
        <span className="font-medium">{capabilityLabel}</span> — il consenso scade{" "}
        {formatItalianDays(daysRemaining)}. Rinnovalo prima per non perdere l'accesso ai dati.
      </p>
      {onRenew ? (
        <Button
          size="sm"
          variant={urgency === "critical" ? "primary" : "ghost"}
          onClick={onRenew}
          className="shrink-0"
        >
          Rinnova
        </Button>
      ) : null}
    </aside>
  );
};

ConsentExpiryBanner.displayName = "ConsentExpiryBanner";
